import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import "../Styles/login.css";
import Axios from "axios";
import Img from "../Assets/icons/VELOXLogo.png";
import * as yup from "yup";
import { ErrorMessage, Formik, Form, Field } from "formik";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";

/**
 * MDLoginPage
 * - Access ONLY via "/:mdId/md-login"
 * - Grabs mdId from the path and stores it in localStorage('md_ref') for later use.
 * - No backend call to validate/fetch MD.
 */
export default function MDLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const { mdId } = useParams();

  const setOnce = (key, value) => {
    try {
      if (localStorage.getItem(`${key}_locked`) === "1") return; // already locked
      localStorage.setItem(key, value);
      localStorage.setItem(`${key}_locked`, "1");
    } catch (_) { /* ignore storage errors */ }
  };

  // If someone hits this page without mdId in the path, bounce home
  useEffect(() => {
    if (!mdId) navigate("/", { replace: true });
  }, [mdId, navigate]);

  // Persist MD so it survives the auth flow
  useEffect(() => {
    if (mdId) localStorage.setItem("md_ref", mdId);
  }, [mdId]);

  // YubiKey dialog state
  const [openYubikeyDialog, setOpenYubikeyDialog] = useState(false);
  const [yubikeyCode, setYubikeyCode] = useState("");
  const yubikeyInputRef = useRef(null);
  const dialogContentRef = useRef(null);

  // Already logged in? redirect away
  useEffect(() => {
    if (localStorage.getItem("@customer") === "true") {
      const rawNext = new URLSearchParams(location.search).get("next");
      const decodedNext = rawNext ? decodeURIComponent(rawNext) : null;
      navigate(decodedNext || `/${mdId}/dashboard`, { replace: true });
    }
  }, [location.search, navigate, mdId]);

  const validationsLogin = yup.object().shape({
    email: yup.string().email("Invalid email").required("Email is mandatory"),
    password: yup
      .string()
      .min(8, "Password must be at least 8 characters")
      .required("Password is mandatory"),
  });

  const handleLogin = (values) => {
    Axios.post(`/customer_login`, {
      email: values.email,
      password: values.password,
      mdId: mdId,
      // Optional: include MD if/when the backend wants it
      // md_id: mdId,
    })
      .then((res) => {
        if (res.data?.success) {
          toast.success("Verified your credential, please verify your YubiKey!");
          localStorage.setItem("userEmail", values.email);
          setOpenYubikeyDialog(true);
        } else {
          toast.error(res.data?.msg || "Incorrect email or password");
        }
      })
      .catch(() => toast.error("Your Email and password didn't match!"));
  };

  const handleYubikeyVerification = () => {
    const email = localStorage.getItem("userEmail");
    Axios.post(`/customer_login_verify_yubikey`, {
      email,
      yubikeyContent: yubikeyCode,
    })
      .then((res) => {
        if (res.data?.success) {
          localStorage.setItem("@customer", "true");
          localStorage.setItem("mdId", mdId);
          localStorage.setItem("role", res.data.role); // default to customer role
          if (res.data.siId) {
            setOnce("siId", String(res.data.siId));
          }
          const rawNext = new URLSearchParams(location.search).get("next");
          const decodedNext = rawNext ? decodeURIComponent(rawNext) : null;
          window.location.replace(decodedNext || `/${mdId}/dashboard`);
        } else {
          toast.error(res.data?.msg || "VELOX Cloud Key verification failed");
          yubikeyInputRef.current?.focus();
        }
        setYubikeyCode("");
      })
      .catch(() => {
        setYubikeyCode("");
        yubikeyInputRef.current?.focus();
      });
  };

  // keep focus on input while dialog is open (for fast YubiKey OTP paste)
  useEffect(() => {
    const handleDialogClick = (e) => {
      if (
        dialogContentRef.current &&
        yubikeyInputRef.current &&
        !yubikeyInputRef.current.contains(e.target)
      ) {
        yubikeyInputRef.current.focus();
      }
    };
    if (openYubikeyDialog) {
      yubikeyInputRef.current?.focus();
      document.addEventListener("click", handleDialogClick);
    }
    return () => document.removeEventListener("click", handleDialogClick);
  }, [openYubikeyDialog]);

  return (
    <div>
      <div className="logo-VELOX">
        <img src={Img} alt="VELOX Life Safety Inc." className="logo" />
        <button className="login-back-botton" onClick={() => navigate(-1)}>
          BACK
        </button>
      </div>

      <hr className="login-breakline" />

      {/* Context strip (optional) */}
      <section style={{ textAlign: "center", marginBottom: 16 }}>
        {mdId && (
          <div style={{ fontSize: 12, opacity: 0.75 }}>
            Master Distributor: <strong>{mdId}</strong>
          </div>
        )}
      </section>

      <div className="login-card">
        <h1 className="login">Login</h1>
        <Formik
          initialValues={{ email: "", password: "" }}
          onSubmit={handleLogin}
          validationSchema={validationsLogin}
        >
          <Form className="login-form">
            <div className="login-form-group">
              <Field
                name="email"
                type="email"
                className="login-form-email-field"
                placeholder="Email"
                autoComplete="username"
              />
              <ErrorMessage component="span" name="email" className="form-error" />
            </div>

            <div className="login-form-group">
              <Field
                name="password"
                type="password"
                className="login-form-password-field"
                placeholder="Password"
                autoComplete="current-password"
              />
              <ErrorMessage component="span" name="password" className="form-error" />
            </div>

            <button className="verify-yubikey-button" type="submit">
              Verify Your YubiKey
            </button>
          </Form>
        </Formik>
      </div>

      <Dialog open={openYubikeyDialog} onClose={() => setOpenYubikeyDialog(false)}>
        <DialogTitle>YubiKey Verification</DialogTitle>
        <DialogContent ref={dialogContentRef}>
          <p>Please insert your YubiKey, touch it, and then verify.</p>
          <input
            ref={yubikeyInputRef}
            type="text"
            className="yubikeycode-field-hidden"
            placeholder="Enter YubiKey Code"
            value={yubikeyCode}
            onChange={(e) => setYubikeyCode(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenYubikeyDialog(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleYubikeyVerification} color="primary" disabled={!(yubikeyCode && yubikeyCode.length >= 12)}>
            Verify
          </Button>
        </DialogActions>
      </Dialog>

      <ToastContainer />
    </div>
  );
}
